import Big from 'big.js'

import { Dashboardv99990101Api } from '@sequencehq/api/dashboard/v99990101'
import { Invoice } from 'Invoices/types'

import {
  INELIGIBILITY_FALLBACK_COPY,
  INELIGIBILITY_REASON_COPY,
  UNKNOWN_INELIGIBILITY_KEY
} from './applyCreditNote.constants'

/**
 * Money here is a major-unit decimal string ('1000.00'), the same
 * representation the invoice/credit-note APIs and `toMoney` use. All
 * arithmetic runs through Big.js on those strings so decimals stay exact.
 */

export type CreditNoteApplicationEligibilityReason =
  Dashboardv99990101Api.GetCreditNoteApplicationEligibility.CreditNoteApplicationEligibilityReason

export type ApplyCreditRowEligibility =
  | 'pending'
  | 'eligible'
  | { ineligible: CreditNoteApplicationEligibilityReason[] }

export type ApplyCreditRow = {
  rowId: string
  invoiceId: string
  invoiceNumber: string
  dueDate?: string
  invoiceBalance: string
  invoiceBalanceVersion: number
  amount: string
  eligibility: ApplyCreditRowEligibility
}

export type IneligibilityMessage = { key: string; message: string }

/**
 * Reason copy for an ineligible row. A reason we hold no copy for, and a
 * reasonless `eligible: false`, both collapse to one fallback line so the row
 * never blocks submit in silence.
 */
export const toIneligibilityMessages = (
  reasons: CreditNoteApplicationEligibilityReason[]
): IneligibilityMessage[] => {
  const fallback = {
    key: UNKNOWN_INELIGIBILITY_KEY,
    message: INELIGIBILITY_FALLBACK_COPY
  }

  const messages = reasons.reduce<IneligibilityMessage[]>((acc, reason) => {
    const copy = INELIGIBILITY_REASON_COPY[reason]
    const next = copy ? { key: reason, message: copy } : fallback

    // Two unknown reasons would otherwise repeat the same fallback line.
    return acc.some(existing => existing.key === next.key)
      ? acc
      : [...acc, next]
  }, [])

  return messages.length > 0 ? messages : [fallback]
}

/** Parse free-text input to a 2dp decimal string, rounding half-up. Null when empty/invalid. */
export const parseAmount = (input: string): string | null => {
  const trimmed = input.trim()
  if (!trimmed) return null

  try {
    return new Big(trimmed).toFixed(2, Big.roundHalfUp)
  } catch {
    return null
  }
}

/**
 * Balance responses quote money as a raw JSON number, so it's converted at the
 * boundary to the decimal string everything downstream works in.
 */
export const balanceValueToAmount = (value: number): string =>
  new Big(value).toFixed(2)

/** The inverse: the request quotes `amount` as a raw JSON number too. */
export const amountToRequestValue = (amount: string): number =>
  new Big(amount).toNumber()

/**
 * True only for a balance that parses to a positive amount. The credit note
 * read model omits the balance for some credit notes, so anything unparseable
 * counts as no balance rather than throwing.
 */
export const hasPositiveBalance = (balance: string | undefined): boolean => {
  if (!balance) return false

  try {
    return new Big(balance).gt(0)
  } catch {
    return false
  }
}

/** Clamp an amount to a floor of zero, as a 2dp string. */
const clampToZero = (value: Big): string =>
  (value.lt(0) ? new Big(0) : value).toFixed(2)

/**
 * Live feedback for a row's amount field. Only 'ok' allows submit for that
 * row (see canSubmitApplyCredits).
 */
export type RowAmountFeedback =
  | { type: 'empty' }
  | { type: 'nonPositive' }
  | { type: 'exceedsInvoice'; invoiceBalance: string }
  | { type: 'exceedsCredit'; remainingCredit: string }
  | { type: 'ok'; amount: string }

export const getRowAmountFeedback = (
  amount: string,
  invoiceBalance: string,
  remainingCredit: string
): RowAmountFeedback => {
  const parsed = parseAmount(amount)
  if (parsed === null) return { type: 'empty' }

  const amountValue = new Big(parsed)
  if (amountValue.lte(0)) return { type: 'nonPositive' }
  if (amountValue.gt(invoiceBalance))
    return { type: 'exceedsInvoice', invoiceBalance }
  if (amountValue.gt(remainingCredit))
    return { type: 'exceedsCredit', remainingCredit }

  return { type: 'ok', amount: parsed }
}

export type ApplyTotals = {
  appliedByRow: Array<{ rowId: string; invoiceNumber: string; amount: string }>
  totalApplied: string
  creditRemaining: string
}

export const getApplyTotals = (
  creditNoteBalance: string,
  rows: ApplyCreditRow[]
): ApplyTotals => {
  const appliedByRow = rows.reduce<ApplyTotals['appliedByRow']>((acc, row) => {
    const parsed = parseAmount(row.amount)
    if (parsed === null || new Big(parsed).lte(0)) return acc

    return [
      ...acc,
      { rowId: row.rowId, invoiceNumber: row.invoiceNumber, amount: parsed }
    ]
  }, [])

  const totalApplied = appliedByRow
    .reduce((sum, row) => sum.plus(row.amount), new Big(0))
    .toFixed(2)

  return {
    appliedByRow,
    totalApplied,
    creditRemaining: clampToZero(new Big(creditNoteBalance).minus(totalApplied))
  }
}

/** Credit still available to a row: the balance less every other row's amount. */
export const remainingCreditForRow = ({
  creditNoteBalance,
  totals,
  rowId
}: {
  creditNoteBalance: string
  totals: ApplyTotals
  rowId: string
}): string => {
  const ownAmount =
    totals.appliedByRow.find(applied => applied.rowId === rowId)?.amount ?? '0'

  return clampToZero(
    new Big(creditNoteBalance).minus(totals.totalApplied).plus(ownAmount)
  )
}

/**
 * Eligible when UNPAID/PARTIALLY_PAID; list items omit `balance`, so
 * balance-based checks (positive balance, partial-unsettled) only apply once
 * it's known.
 */
export const isInvoiceEligibleOption = (
  invoice: Pick<Invoice, 'paymentStatus' | 'balance' | 'grossTotal'>
): boolean => {
  const isPayableStatus =
    invoice.paymentStatus === 'UNPAID' ||
    invoice.paymentStatus === 'PARTIALLY_PAID'

  if (!isPayableStatus) return false
  if (invoice.balance === undefined) return true

  const hasOutstandingBalance = new Big(invoice.balance).gt(0)
  const isUnpaidPartialPayment =
    invoice.paymentStatus === 'PARTIALLY_PAID' &&
    new Big(invoice.balance).eq(invoice.grossTotal)

  return hasOutstandingBalance && !isUnpaidPartialPayment
}

/** Seed a newly-added row's amount as the smaller of remaining credit and invoice balance, 2dp. */
export const seedRowAmount = (
  creditRemaining: string,
  invoiceBalance: string
): string => {
  const remaining = new Big(creditRemaining)
  const balance = new Big(invoiceBalance)
  return (remaining.lt(balance) ? remaining : balance).toFixed(2)
}

/**
 * Gated on exactly what the rows display. The per-row ceiling is the credit the
 * other rows leave over, so it caps the total too — no separate sum check.
 */
export const canSubmitApplyCredits = ({
  rows,
  creditNoteBalance,
  totals
}: {
  rows: ApplyCreditRow[]
  creditNoteBalance: string
  totals: ApplyTotals
}): boolean => {
  if (rows.length === 0) return false

  const invoiceIds = rows.map(row => row.invoiceId)
  const hasDuplicateInvoices = new Set(invoiceIds).size !== invoiceIds.length
  if (hasDuplicateInvoices) return false

  return rows.every(row => {
    const feedback = getRowAmountFeedback(
      row.amount,
      row.invoiceBalance,
      remainingCreditForRow({ creditNoteBalance, totals, rowId: row.rowId })
    )

    return feedback.type === 'ok' && row.eligibility === 'eligible'
  })
}
